import type { ReportTemplate } from '~/gql/reports/reports'
import { RoleEnum } from '@zeus/index'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '~/components/ConfirmDialog'
import Spinner from '~/components/Spinner'
import { useAuth } from '~/helpers/store/auth'
import ReportScheduleCard from './ReportScheduleCard'
import ReportTemplateEditor from './ReportTemplateEditor'
import ReportTemplatesList from './ReportTemplatesList'
import { useReportTemplates } from './use-report-templates'

/** Ce que l'éditeur affiche : un nouveau modèle, ou celui qu'on modifie. Change de clé à chaque bascule. */
type Editing = { key: number, template: ReportTemplate | null }

/**
 * Les rapports configurables (#163) : la liste des modèles, à télécharger, et
 * pour l'admin l'éditeur qui les compose — un nouveau modèle par défaut, ou
 * celui choisi dans la liste.
 */
function ReportsPage() {
  const { t } = useTranslation()
  const isAdmin = useAuth(state => state.payload?.role) === RoleEnum.Admin
  const reports = useReportTemplates()
  const [editing, setEditing] = useState<Editing>({ key: 0, template: null })
  const [dirty, setDirty] = useState(false)
  const [pending, setPending] = useState<ReportTemplate | null | undefined>(undefined)
  const onDirtyChange = useCallback((value: boolean) => setDirty(value), [])

  const open = (template: ReportTemplate | null) => setEditing(current => ({ key: current.key + 1, template }))
  /** Changer de modèle recharge l'éditeur : avec des modifications non enregistrées, l'admin confirme d'abord. */
  const requestOpen = (template: ReportTemplate | null) => (dirty ? setPending(template) : open(template))

  if (reports.error) {
    return <p role="alert" className="p-6 text-center text-sm text-destructive">{t('reports.loadError')}</p>
  }
  if (reports.loading && reports.templates.length === 0) {
    return (
      <div className="flex justify-center p-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <ReportTemplatesList
        templates={reports.templates}
        canManage={isAdmin}
        downloadingId={reports.downloadingId}
        onDownload={template => void reports.download(template)}
        onEdit={requestOpen}
        onDuplicate={template => void reports.duplicate(template, t('reports.copyName', { name: template.name }))}
        onRemove={async (template) => {
          // L'éditeur ne lâche le modèle qu'une fois la suppression faite : un échec garde l'édition.
          if (await reports.remove(template) && editing.template?.id === template.id) open(null)
        }}
      />
      {isAdmin && (
        <ReportTemplateEditor
          key={editing.key}
          template={editing.template}
          onSaved={() => {
            void reports.refetch()
            open(null)
          }}
          onDirtyChange={onDirtyChange}
          onReset={() => requestOpen(null)}
        />
      )}
      {isAdmin && (
        // Le modèle tel que la liste le relit : son envoi y est à jour après chaque enregistrement.
        <ReportScheduleCard
          template={reports.templates.find(template => template.id === editing.template?.id) ?? null}
          onSaved={() => void reports.refetch()}
        />
      )}
      <ConfirmDialog
        open={pending !== undefined}
        title={t('reports.discardTitle')}
        confirmLabel={t('reports.discard')}
        onCancel={() => setPending(undefined)}
        onConfirm={() => {
          if (pending !== undefined) open(pending)
          setPending(undefined)
        }}
      >
        {t('reports.discardBody')}
      </ConfirmDialog>
    </div>
  )
}

export default ReportsPage
